const ZOOM_MIN = 1
// Start with the whole map visible on mobile/tablet and allow returning
// to this overview after zooming in to select closely spaced markers.
const MOBILE_ZOOM_MIN = 1
const ZOOM_MAX = 8
const MOBILE_ZOOM_MAX = 16
const ZOOM_STEP = 1
const FOCUS_SCALE = ZOOM_MAX
const DRAG_THRESHOLD = 6
// tablet and phone now both get the full pinch/pan/zoom treatment (same as
// desktop) — this only decides the tooltip's presentation (full-cover vs.
// anchored popup) and the marker/toolbar sizing tiers in Landkarte.scss.
// Kept in sync with Landkarte.scss's landkarte-not-desktop mixin.
const NOT_DESKTOP_QUERY = '(max-width: 991px)'

export default function InitLandkarte() {
    // the map is absolutely positioned/transformed for pan+zoom — in the
    // backend the hotspot list needs normal in-flow layout instead (so
    // editors can click into each one and Neos's own "add content"
    // dropzone works), see Landkarte.scss's ".neos-backend &" override
    if (window.neos || document.body.classList.contains('neos-backend')) {
        return
    }

    // real touch hardware, not spoofable by "Request Desktop Website"
    // mode (unlike the (hover:none)/(pointer:coarse) media features,
    // which iPadOS deliberately reports as desktop-like in that mode —
    // confirmed as the actual cause of tablets wrongly getting the
    // desktop UI here). Kept in sync with Landkarte.scss's
    // "html.landkarte--touch" selector in the *-not-desktop/tablet mixins.
    const isTouchDevice = navigator.maxTouchPoints > 0 || 'ontouchstart' in window
    document.documentElement.classList.toggle('landkarte--touch', isTouchDevice)

    // tablet and phone get the full-cover tooltip instead of desktop's
    // small anchored popup — there's much less room to anchor it sensibly
    // once the module is the width of the screen. Checked live at the
    // moment a marker is clicked (not cached once at load) so it can't go
    // stale across an orientation change or a resize after page load.
    const isTooltipFullscreen = () => window.matchMedia(NOT_DESKTOP_QUERY).matches || isTouchDevice

    // checked once at load (not live like isTooltipFullscreen) - this only
    // sets the *initial* framing, so it doesn't need to react to a later
    // resize/orientation change the way the tooltip presentation does
    const zoomMin = isTooltipFullscreen() ? MOBILE_ZOOM_MIN : ZOOM_MIN
    const getZoomMax = () => isTooltipFullscreen() ? MOBILE_ZOOM_MAX : ZOOM_MAX

    document.querySelectorAll('[data-landkarte]').forEach((root) => {
        const viewport = root.querySelector('[data-landkarte-viewport]')
        const rendering = root.querySelector('[data-landkarte-rendering]')
        const zoomInButton = root.querySelector('[data-landkarte-zoom-in]')
        const zoomOutButton = root.querySelector('[data-landkarte-zoom-out]')
        const hotspots = root.querySelectorAll('[data-landkarte-hotspot]')

        let focusOnHotspot = () => {}

        if (viewport && rendering) {
            let scale = zoomMin
            let translateX = 0
            let translateY = 0

            const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

            // &__rendering's transform-origin is the top-left corner (see
            // Landkarte.scss), so a local point at pixel X maps to screen
            // position "translateX + scale*X". For the scaled image to
            // fully cover the viewport (no visible gap at either edge),
            // translateX must stay within [width - scale*width, 0] — NOT
            // a symmetric ±range around 0, since the fixed point is the
            // left edge, not the center. Getting this asymmetric bound
            // wrong is exactly what let the map pan past its own edge.
            const clampTranslation = () => {
                const viewportRect = viewport.getBoundingClientRect()
                const minX = viewportRect.width - scale * viewportRect.width
                const minY = viewportRect.height - scale * viewportRect.height
                translateX = clamp(translateX, minX, 0)
                translateY = clamp(translateY, minY, 0)
            }

            const applyTransform = () => {
                clampTranslation()
                rendering.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`
                // markers/tooltips counter-scale against this so they stay a
                // constant on-screen size regardless of map zoom (Landkarte.scss)
                root.style.setProperty('--landkarte-inverse-scale', String(1 / scale))
                // non-primary markers are hidden by default (Landkarte.scss)
                // and only revealed once zoomed in past the initial view —
                // covers the zoom buttons, wheel zoom, and a primary
                // marker's own click-to-focus zoom, since all three end up
                // calling applyTransform()
                root.classList.toggle('landkarte--zoomed', scale > zoomMin)
            }

            // keep the point under the cursor/pinch-midpoint/center
            // stationary while zooming — with a top-left transform-origin,
            // the local map point currently under (originX, originY) is
            // (originX - translateX) / previousScale; solve for the new
            // translateX that keeps that same local point under the same
            // screen position at the new scale. Takes an absolute target
            // scale so both the buttons/wheel (via zoomBy below) and pinch
            // (which tracks an absolute scale ratio, not a per-event delta)
            // can share this.
            const zoomTo = (newScale, originX, originY) => {
                const previousScale = scale
                scale = clamp(newScale, zoomMin, getZoomMax())

                if (scale === previousScale) {
                    return
                }

                const viewportRect = viewport.getBoundingClientRect()
                const originPointX = originX ?? viewportRect.width / 2
                const originPointY = originY ?? viewportRect.height / 2
                const localX = (originPointX - translateX) / previousScale
                const localY = (originPointY - translateY) / previousScale
                translateX = originPointX - scale * localX
                translateY = originPointY - scale * localY

                applyTransform()
            }

            const zoomBy = (delta, originX, originY) => zoomTo(scale + delta, originX, originY)

            // Center the initial view if a minimum above 1 is configured.
            if (scale > ZOOM_MIN) {
                const viewportRect = viewport.getBoundingClientRect()
                translateX = (viewportRect.width - scale * viewportRect.width) / 2
                translateY = (viewportRect.height - scale * viewportRect.height) / 2
                applyTransform()
            }

            // clicking a marker gently zooms in and centers on that point,
            // instead of an abrupt cut — reuses the same transition as the
            // +/- buttons (see Landkarte.scss's transition on &__rendering)
            focusOnHotspot = (hotspot) => {
                const viewportRect = viewport.getBoundingClientRect()
                const leftPercent = parseFloat(hotspot.style.left) || 50
                const topPercent = parseFloat(hotspot.style.top) || 50
                const localX = (leftPercent / 100) * viewportRect.width
                const localY = (topPercent / 100) * viewportRect.height

                scale = clamp(Math.max(scale, FOCUS_SCALE), zoomMin, getZoomMax())
                translateX = viewportRect.width / 2 - scale * localX
                translateY = viewportRect.height / 2 - scale * localY
                applyTransform()
            }

            zoomInButton?.addEventListener('click', () => zoomBy(ZOOM_STEP))
            zoomOutButton?.addEventListener('click', () => zoomBy(-ZOOM_STEP))

            viewport.addEventListener('wheel', (event) => {
                event.preventDefault()
                const rect = viewport.getBoundingClientRect()
                zoomBy(event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP, event.clientX - rect.left, event.clientY - rect.top)
            }, { passive: false })

            // pointer events unify mouse drag + touch pan in one handler.
            // Panning only actually starts once the pointer has moved past
            // DRAG_THRESHOLD — otherwise a plain click on a marker (which
            // always has a pixel or two of jitter between down and up)
            // would shift the map out from under the cursor and the click
            // on the marker button would never register.
            let isDragCandidate = false
            let isPanning = false
            let panStartX = 0
            let panStartY = 0
            let panOriginX = 0
            let panOriginY = 0

            // two-finger pinch-to-zoom on touch devices, tracked alongside
            // the single-pointer pan above via the Pointer Events already
            // in use — activePointers holds every pointer currently down
            // on the viewport, keyed by pointerId
            const activePointers = new Map()
            let pinchStartDistance = 0
            let pinchStartScale = 1
            let pinchMidX = 0
            let pinchMidY = 0

            const getPointerDistance = () => {
                const [a, b] = [...activePointers.values()]
                return Math.hypot(a.x - b.x, a.y - b.y)
            }

            const getPointerMidpoint = () => {
                const [a, b] = [...activePointers.values()]
                return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
            }

            // capturing the pointer immediately on every pointerdown (even
            // a plain click that never turns into a pan) redirects the
            // resulting click event to the viewport instead of the actual
            // marker button — silently breaking marker clicks. Capture is
            // only taken once a gesture is confirmed as a real pan/pinch,
            // same as the pre-pinch behavior below (past DRAG_THRESHOLD).
            const capturePointer = (pointerId) => {
                try {
                    viewport.setPointerCapture(pointerId)
                } catch {
                    // Safari can throw for touch pointerIds in some versions —
                    // capture is a nice-to-have (keeps tracking if the pointer
                    // leaves the element), not required for pan/pinch itself
                }
            }

            viewport.addEventListener('pointerdown', (event) => {
                activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY })

                if (activePointers.size === 2) {
                    // a second finger landed — this is unambiguously a
                    // pinch, not a click, so capture both pointers right away
                    activePointers.forEach((_, pointerId) => capturePointer(pointerId))

                    isDragCandidate = false
                    isPanning = false
                    viewport.classList.add('is-panning')
                    rendering.classList.add('is-panning')

                    const rect = viewport.getBoundingClientRect()
                    const mid = getPointerMidpoint()
                    pinchStartDistance = getPointerDistance()
                    pinchStartScale = scale
                    pinchMidX = mid.x - rect.left
                    pinchMidY = mid.y - rect.top
                    return
                }

                if (activePointers.size > 2 || scale <= zoomMin) {
                    return
                }

                isDragCandidate = true
                isPanning = false
                panStartX = event.clientX
                panStartY = event.clientY
                panOriginX = translateX
                panOriginY = translateY
            })

            viewport.addEventListener('pointermove', (event) => {
                if (activePointers.has(event.pointerId)) {
                    activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY })
                }

                if (activePointers.size === 2) {
                    if (pinchStartDistance > 0) {
                        zoomTo(pinchStartScale * (getPointerDistance() / pinchStartDistance), pinchMidX, pinchMidY)
                    }
                    return
                }

                if (!isDragCandidate) {
                    return
                }

                const dx = event.clientX - panStartX
                const dy = event.clientY - panStartY

                if (!isPanning) {
                    if (Math.hypot(dx, dy) < DRAG_THRESHOLD) {
                        return
                    }

                    isPanning = true
                    viewport.classList.add('is-panning')
                    rendering.classList.add('is-panning')
                    capturePointer(event.pointerId)
                }

                translateX = panOriginX + dx
                translateY = panOriginY + dy
                applyTransform()
            })

            const stopPanning = () => {
                isDragCandidate = false
                isPanning = false
                viewport.classList.remove('is-panning')
                rendering.classList.remove('is-panning')
            }

            const releasePointer = (event) => {
                activePointers.delete(event.pointerId)

                if (activePointers.size < 2) {
                    pinchStartDistance = 0
                }

                if (activePointers.size === 0) {
                    stopPanning()
                }
            }

            viewport.addEventListener('pointerup', releasePointer)
            viewport.addEventListener('pointercancel', releasePointer)
        }

        // hotspot tooltips
        const closeAllTooltips = (except) => {
            hotspots.forEach((hotspot) => {
                if (hotspot !== except) {
                    hotspot.classList.remove('landkarte__hotspot--tooltip-active')
                }
            })
        }

        // below desktop the tooltip is a fixed bottom sheet (Landkarte.scss)
        // — but position:fixed is computed against the nearest ancestor
        // that has a `transform`, not the real browser viewport, if one
        // exists in between. &__hotspot always has one (it's how markers
        // stay centered on their point), so a tooltip left nested inside it
        // renders as a tiny box pinned to that marker instead of a real
        // bottom sheet. Moving the tooltip node out to &__frame (which has
        // no transform) before showing it sidesteps that entirely; it's
        // moved back to its original spot on close so desktop's anchored-
        // popup layout (which relies on the nested position) stays intact.
        const frame = root.querySelector('.landkarte__frame')
        let movedTooltip = null

        const restoreMovedTooltip = () => {
            if (!movedTooltip) {
                return
            }

            movedTooltip.el.classList.remove('landkarte__tooltip--open')
            movedTooltip.originalParent.insertBefore(movedTooltip.el, movedTooltip.originalNextSibling)
            movedTooltip = null
        }

        hotspots.forEach((hotspot) => {
            const marker = hotspot.querySelector('[data-landkarte-marker]')
            const closeButton = hotspot.querySelector('[data-landkarte-tooltip-close]')
            const tooltip = hotspot.querySelector('[data-landkarte-tooltip]')

            marker?.addEventListener('click', (event) => {
                event.stopPropagation()

                if (isTooltipFullscreen()) {
                    const isOpen = movedTooltip?.el === tooltip

                    restoreMovedTooltip()

                    if (!isOpen && frame && tooltip) {
                        movedTooltip = { el: tooltip, originalParent: hotspot, originalNextSibling: tooltip.nextSibling }
                        frame.appendChild(tooltip)
                        tooltip.classList.add('landkarte__tooltip--open')
                        // same as desktop: gently zoom/center on the clicked
                        // point instead of just popping the modal open
                        focusOnHotspot(hotspot)
                    }

                    return
                }

                const isActive = hotspot.classList.contains('landkarte__hotspot--tooltip-active')
                closeAllTooltips(hotspot)
                hotspot.classList.toggle('landkarte__hotspot--tooltip-active', !isActive)

                if (!isActive) {
                    focusOnHotspot(hotspot)
                }
            })

            closeButton?.addEventListener('click', (event) => {
                event.stopPropagation()
                hotspot.classList.remove('landkarte__hotspot--tooltip-active')
                restoreMovedTooltip()
            })
        })

        document.addEventListener('click', () => {
            closeAllTooltips(null)
            restoreMovedTooltip()
        })
    })
}
